import {Inject, Injectable} from '@angular/core';
import * as _ from "lodash";
import {
    DataPoint,
    HeatingCurveChartParams,
    HeatingCurveParam,
    HeatingCurveParams,
    HeatingCurvePoint,
    HeatingShiftTable,
    ICanvasJS,
    MenuElementType28,
    MenuElementType29,
    MenuElementType30, MenuElementType31
} from "../../models";
import {ControlService} from "./control.service";
import {DOCUMENT} from "@angular/common";
import {CanvasService} from "./canvas.service";
import {SendDataService} from "../send-data.service";
import {ParametersService} from "../parameters.service";
import {ChangeContext} from "@angular-slider/ngx-slider";
import {FormatPipe} from "../../shared";

@Injectable({
    providedIn: 'root'
})
export class HeatingCurveService {
    chart: ICanvasJS;
    private const1: number = 0;
    private const2: number = 0;
    private const3: number = 0;
    private const4: number = 0;
    private const5: number = 0;
    private counter: number = 0;
    private dataPoints: DataPoint[] = [];
    heatingCurveTable: HeatingCurvePoint[] = [];
    heatingShiftTable: HeatingShiftTable = {
        slope: 0,
        shift: 0,
        max: 0,
    };
    private reverse: boolean = false;
    private setTemp: number = 0;
    shift: number | undefined;
    slope: number | undefined;
    private xMax: number = 200;
    private xMin: number = -200;
    private y: number = 0;
    private yTemp: number = 0;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private control: ControlService,
        private canvas: CanvasService,
        private parameters: ParametersService,
        private sendData: SendDataService,
        private format: FormatPipe,
    ) {
    }

    setChartColor(): string {
        if (this.parameters.urlCompany === 'klimor') return '#0061aa'; // $primary

        return "#ff3e17";
    }

    createHeatingCurveChart(): void {
        this.dataPoints = [];

        const elementVal = this.control.elementVal.getValue() as MenuElementType29;
        this.slope = elementVal.params.slope;
        this.shift = elementVal.params.shift;
        this.const1 = elementVal.params.constant1 / 100;
        this.const2 = elementVal.params.constant2 / 100;
        this.const3 = elementVal.params.constant3 / 100;
        this.const4 = elementVal.params.constant4 / 100;
        this.const5 = elementVal.params.constant5 / 100;

        this.xMin = elementVal.params.xMin ? elementVal.params.xMin * 10 : -200;
        this.xMax = elementVal.params.xMax ? elementVal.params.xMax * 10 : 200;
        this.setTemp = elementVal.params.setTemp / 10;

        this.generateHeatingCurveChart();
    }

    generateHeatingCurveChart(): void {
        const elementVal = this.control.elementVal.getValue() as MenuElementType29;
        this.slope = this.slope === undefined ? elementVal.params.slope : this.slope;
        this.shift = this.shift === undefined ? elementVal.params.shift : this.shift;

        this.generateDataPoints();

        this.chart = new (window as any).CanvasJS.Chart("chartContainer", {
            backgroundColor: '#f8f8f8',
            axisY:{
                includeZero: false,
                gridThickness: 1,
                suffix: '℃',
                minimum: elementVal.params.yMin
                    ? elementVal.params.yMin
                    : elementVal.params.resultingTemperatureMin - 10,
                maximum: elementVal.params.yMax
                    ? elementVal.params.yMax
                    : elementVal.params.resultingTemperatureMax + 10
            },

            axisX:{
                reversed: this.reverse,
                gridThickness: 1,
                suffix: '℃',
                minimum: elementVal.params.xMin
                    ? this.xMin < this.xMax
                        ? this.xMin/10
                        : this.xMax/10
                    : -20,
                maximum: elementVal.params.xMax
                    ? this.xMin < this.xMax
                        ? this.xMax/10
                        : this.xMin/10
                    : 20
            },
            toolTip:{
                enabled: false
            },
            data: [
                {
                    type: "line",
                    lineColor: this.setChartColor(),
                    highlightEnabled: false,
                    lineThickness: 4,
                    markerSize: 0,
                    dataPoints: this.dataPoints
                }
            ]
        });

        this.chart.render();
    }

    private generateDataPoints(): void {
        this.counter = 0;

        if (this.xMin < this.xMax) {
            this.reverse = false;

            for (let i = this.xMin; i <= this.xMax; i++) {
                this.calculatePoint(i);
            }
        } else {
            this.reverse = true;
            for (let i = this.xMin; i >= this.xMax; i--) {
                this.calculatePoint(i);
            }
        }

        for (let i = this.dataPoints.length; i > this.counter; i--) {
            this.dataPoints.pop();
        }
    }

    private calculatePoint(i: number): void {
        const elementVal = this.control.elementVal.getValue() as MenuElementType29;
        switch (elementVal.params.patternId) {
            case 1:
                this.yTemp = this.slope / 10 * (((20 + this.shift) - i / 10) + (20 + this.shift));
                break;
            default:
                this.yTemp = (this.const1 * (this.slope / 10))
                    * (Math.pow(this.setTemp, ((i / 10) / (this.const2 - ((i / 10) * this.const3)))))
                    * ((this.const4 - (i / 10)) * this.const5)
                    + this.setTemp + this.shift;
        }

        this.y = this.yTemp > elementVal.params.resultingTemperatureMax
            ? elementVal.params.resultingTemperatureMax
            : this.yTemp < elementVal.params.resultingTemperatureMin
                ? elementVal.params.resultingTemperatureMin
                : this.yTemp;

        if ((this.yTemp > elementVal.params.resultingTemperatureMax - 0.3
                && this.yTemp < elementVal.params.resultingTemperatureMax + 0.3)
            || (this.yTemp < elementVal.params.resultingTemperatureMin + 0.3
                && this.yTemp > elementVal.params.resultingTemperatureMin - 0.3)
            || (i / 10) % 1 === 0) {

            if ((i / 10) % 5 == 0) {
                this.dataPoints[this.counter] = {
                    x: (i / 10),
                    y: this.y,
                    indexLabel: ' ' + Math.floor(this.y) + ' ',
                    indexLabelFontWeight: 'bold',
                    indexLabelFontSize: 15,
                    indexLabelFontColor: this.setChartColor(),
                    markerSize: 10,
                    markerColor: this.setChartColor()
                };
            } else {
                this.dataPoints[this.counter] = {
                    x: (i / 10),
                    y: this.y,
                };
            }

            this.counter++;
        }
    }

    changeChart(shift: number, slope: number): void {
        const elementVal = this.control.elementVal.getValue() as MenuElementType29;
        this.slope += slope;
        if (!(this.slope >= elementVal.params.slopeMin
            && this.slope <= elementVal.params.slopeMax)) {
            this.slope -= slope;
        }

        this.shift += shift;

        if (!(this.shift >= elementVal.params.shiftMin
            && this.shift <= elementVal.params.shiftMax)) {
            this.shift -= shift;
        }

        this.generateDataPoints();
        this.chart.render();
    }

    sendCurveData(data: HeatingCurveParams | HeatingCurveChartParams): void {
        const dataToSend = {
            menuType: this.control.elementVal.getValue().menuType,
            ido: this.control.elementVal.getValue().id,
            data,
        }
        if (this.control.pin) {
            dataToSend.data['pin'] = this.control.pin
            this.control.pin = ''
        }
        this.sendData.sendControlData(dataToSend);
    }

    returnHeatingCurveChart(): void {
        const data = {
            slope: this.slope,
            shift: this.shift
        };
        this.sendCurveData(data);

        if (!this.parameters.demoAccount()) {
            const element = this.control.element.getValue();
            element.params = this.control.elementVal.getValue().params;
            element.duringChange = 't';
            this.control.element.next(element);

            this.control.setsNewValue = this.control.elementVal.getValue().id;
            this.control.hideControl();
        }
    }

    // ---- krzywa grzewcza przesunięcie/nachylenie typ 30 i 28

    getHeatingCurve() {
        const elementVal = this.control.elementVal.getValue() as MenuElementType28 | MenuElementType30;
        this.heatingCurveTable = elementVal.params.points;

        this.drawHeatingCurve();
    }

    setHeatingCurve(index: number, position: 'plus' | 'minus'): void {
        const elementVal = this.control.elementVal.getValue() as MenuElementType28 | MenuElementType30;
        if (position == 'plus') {
            this.heatingCurveTable[index].value =
                this.heatingCurveTable[index].value + 1 > elementVal.params.max
                    ? this.heatingCurveTable[index].value
                    : this.heatingCurveTable[index].value + 1;
        } else {
            this.heatingCurveTable[index].value =
                this.heatingCurveTable[index].value - 1 < elementVal.params.min
                    ? this.heatingCurveTable[index].value
                    : this.heatingCurveTable[index].value - 1;
        }

        this.drawHeatingCurve();
    }

    setHeatingCurveTouch(index: number, changeCtx: ChangeContext): void {
        const elementVal = this.control.elementVal.getValue() as MenuElementType28 | MenuElementType30;
        const change = changeCtx.value;

        if (change < elementVal.params.min) {
            this.heatingCurveTable[index].value = elementVal.params.min;
        } else if (change > elementVal.params.max) {
            this.heatingCurveTable[index].value = elementVal.params.max;
        } else {
            this.heatingCurveTable[index].value = change;
        }

        this.drawHeatingCurve();
    }

    drawHeatingCurve(): void {
        this.canvas.canvasObj = this.document.getElementById('canvas') as HTMLCanvasElement;
        if (!this.canvas.canvasObj) return;

        this.canvas.canvasObj.width = 800;
        this.canvas.canvasObj.height = 250;
        this.canvas.context = this.canvas.canvasObj.getContext('2d');

        const table = _.cloneDeep(this.heatingCurveTable);
        const elementVal = this.control.elementVal.getValue() as MenuElementType28 | MenuElementType30;
        const min = elementVal.params.min;
        const max = elementVal.params.max;
        const range = max - min;
        const yOffset = 250 / range;

        const yFirst = 250 - (table[0].value - (table[1].value - table[0].value) - min) * yOffset;

        this.canvas.drawLine(-100,
            yFirst, 800 / (elementVal.params.points.length + 1),
            250 - ((table[0].value) - min) * yOffset
        );

        const lengthParams = elementVal.params.points.length - 1;

        for (let i = 0; i < lengthParams; i++) {
            const pointValue = elementVal.type === 30
                ? table[i].value + '°'
                : this.format.transform(table[i].value, elementVal.params.unit).toString();

            this.canvas.drawLine(800 / (elementVal.params.points.length + 1) * (i + 1),
                250 - (table[i].value - min) * yOffset,
                (i + 2) * 800 / (elementVal.params.points.length + 1),
                250 - (table[i + 1].value - min) * yOffset
            );
            this.canvas.drawPoint(800 / (elementVal.params.points.length + 1) * (i + 1),
                250 - (table[i].value - min) * yOffset,
                pointValue,
            );
        }

        const yLast = 250 - (table[lengthParams].value
            + (table[lengthParams].value - table[lengthParams - 1].value) - min) * yOffset;

        this.canvas.drawLine(800 / (elementVal.params.points.length + 1) * (lengthParams + 1),
            250 - (table[lengthParams].value - min) * yOffset,
            this.canvas.canvasObj.width + 101, yLast
        );

        const pointValue = elementVal.type === 30
            ? table[lengthParams].value + '°'
            : this.format.transform(table[lengthParams].value, elementVal.params.unit).toString();

        this.canvas.drawPoint(800 / (elementVal.params.points.length + 1) * (lengthParams + 1),
            250 - (table[lengthParams].value - min) * yOffset,
            pointValue
        );
    }

    setDefaultHeatingCurve(): void {
        this.heatingCurveTable.forEach((val) => {
            val.value = val.default;
        });

        this.drawHeatingCurve();
    }

    returnHeatingCurve(): void {
        const params: HeatingCurveParam[] = this.heatingCurveTable.map((param, index) => ({
            id: index,
            value: param.value,
        }));

        const data: HeatingCurveParams = { heating_curve: params };
        this.sendCurveData(data);

        if (!this.parameters.demoAccount()) {
            const element = this.control.element.getValue();
            element.params = this.control.elementVal.getValue().params;
            element.duringChange = 't';
            this.control.element.next(element);

            this.control.setsNewValue = this.control.elementVal.getValue().id;
            this.control.hideControl();
        }
    }

    // ==== heatingCurveShift

    drawHeatingShift(): void {
        const elementVal = this.control.elementVal.getValue() as MenuElementType31;
        this.heatingShiftTable.slope = elementVal.params.slope / 10;
        this.heatingShiftTable.shift = elementVal.params.shift / 10;
        this.heatingShiftTable.max = elementVal.params.maxValue / 10 || 60;

        this.canvas.canvasObj = document.getElementById('canvas') as HTMLCanvasElement;
        this.canvas.context = this.canvas.canvasObj.getContext('2d');
        this.canvas.canvasObj.width = 410;
        this.canvas.canvasObj.height = 430;

        this.canvas.drawHashTable();
        this.changeGraph(0, 0);
    }

    changeGraph(shift: number, slope: number): void {
        const elementVal = this.control.elementVal.getValue() as MenuElementType31;
        let tmp = slope + this.heatingShiftTable.slope;

        this.heatingShiftTable.slope = Math.round(tmp * 100) / 100;

        if (!(this.heatingShiftTable.slope >= elementVal.params.minSlope / 10
            && this.heatingShiftTable.slope <= (elementVal.params.maxSlope) / 10)) {
            this.heatingShiftTable.slope -= slope;
        }

        this.heatingShiftTable.shift += shift;

        if (!(this.heatingShiftTable.shift >= elementVal.params.minShift / 10
            && this.heatingShiftTable.shift <= elementVal.params.maxShift / 10)) {
            this.heatingShiftTable.shift -= shift;
        }

        this.canvas.context.clearRect(0, 0, this.canvas.canvasObj.width, this.canvas.canvasObj.height);
        this.canvas.drawHashTable();
        this.setHeatingPoints(this.heatingShiftTable);
        this.canvas.clear();
        this.canvas.drawTempAll();
    }

    private setHeatingPoints(heatingTable: HeatingShiftTable): void {
        let x: number = 0;
        let y: number = 0;
        let element: number = 0;
        let upper: boolean = false;
        let maxTemp = 405 - heatingTable.max * 4.5;

        heatingTable.x2 = 390;
        heatingTable.y2 = 404 - (heatingTable.slope
            * ((((20 + heatingTable.shift) + 20) + (20 + heatingTable.shift))) * 4.5);

        heatingTable.x1 = 30;
        heatingTable.y1 = 404 - (heatingTable.slope * ((((20 + heatingTable.shift) - 20) + (20 + heatingTable.shift))) * 4.5) < maxTemp
            ? maxTemp
            : 404 - (heatingTable.slope * ((((20 + heatingTable.shift) - 20) + (20 + heatingTable.shift))) * 4.5);

        for (let i = 1; i < 9; i += 0.05) {
            x = i * 45 - 15;
            y = 404 - (heatingTable.slope * ((((20 + heatingTable.shift) - (25 - i * 5)) + (20 + heatingTable.shift))) * 4.5);

            if (y < maxTemp) {
                y = maxTemp;
                if (!upper) {
                    upper = true;
                    heatingTable.x2 = x;
                    heatingTable.y2 = y;
                    this.canvas.drawLine(heatingTable.x1, heatingTable.y1, heatingTable.x2, heatingTable.y2);
                    this.canvas.drawLine(heatingTable.x2, heatingTable.y2, 390, heatingTable.y2);
                }
            }
        }

        if (!upper) {
            this.canvas.drawLine(heatingTable.x1, heatingTable.y1, heatingTable.x2, heatingTable.y2);
        }

        for (let i = 2; i < 9; i += 1) {
            x = i * 45 - 15;
            y = 404 - (heatingTable.slope * ((((20 + heatingTable.shift) - (25 - i * 5)) + (20 + heatingTable.shift))) * 4.5) < maxTemp
                ? maxTemp
                : 404 - (heatingTable.slope * ((((20 + heatingTable.shift) - (25 - i * 5)) + (20 + heatingTable.shift))) * 4.5);
            element = Math.round(90 - y / 4.5);

            if (y > 44 && y < 410) {
                this.canvas.drawPoint(x, y, element + '°');
            }
        }
    }

    setDefaultShift(): void {
        const elementVal = this.control.elementVal.getValue() as MenuElementType31;
        this.heatingShiftTable.slope =  elementVal.params.defaultSlope/10;
        this.heatingShiftTable.shift = elementVal.params.defaultShift/10;
        this.changeGraph(0, 0);
    }

    returnHeatingShift(): void {
        const elementVal = this.control.elementVal.getValue() as MenuElementType31;
        elementVal.params.slope = this.heatingShiftTable.slope * 10;
        elementVal.params.shift = this.heatingShiftTable.shift * 10;
        this.control.elementVal.next(elementVal);

        const dataToSend = {
            slope: this.heatingShiftTable.slope * 10,
            shift: this.heatingShiftTable.shift * 10,
        }

        this.control.returnVal(dataToSend);
    }
}
